import React, { useState } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { products } from '../mock';

export const Products = ({ onProductSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'stainless', name: 'Stainless Steel' },
    { id: 'alloy', name: 'Alloy Steel' },
    { id: 'nickel', name: 'Nickel Alloys' },
    { id: 'special', name: 'Special Alloys' },
    { id: 'duplex', name: 'Duplex Steel' },
    { id: 'finished', name: 'Finished Shafts' }
  ];

  const getFilteredProducts = () => {
    if (selectedCategory === 'all') return products;
    
    const filterMap = {
      'stainless': ['Stainless Steel Pipes & Tubes', 'Stainless Steel Sheets & Plates', 'Round Bars & Rods', 'Flanges & Fittings'],
      'alloy': ['Alloy Steel Round Bars'],
      'nickel': ['Nickel Alloys'],
      'special': ['Special Alloys', 'Forged Components'],
      'duplex': ['Duplex & Super Duplex'],
      'finished': ['Finished Shafts']
    };
    
    return products.filter(product => 
      filterMap[selectedCategory]?.some(name => product.name.includes(name))
    );
  };

  return (
    <section id="products" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Our Product Range
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Comprehensive selection of premium metals and alloys for diverse industrial applications. 
            From standard stainless steels to specialized high-performance alloys.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 lg:gap-4 mb-12 px-4">
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? "default" : "outline"}
              className={`px-3 lg:px-6 py-2 text-sm lg:text-base ${
                selectedCategory === category.id 
                  ? "bg-[#022d47] hover:bg-[#033858] text-white" 
                  : "hover:border-[#022d47] hover:text-[#022d47]"
              }`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
          {getFilteredProducts().map((product) => (
            <Card key={product.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
              <div className="aspect-w-16 aspect-h-9 overflow-hidden rounded-t-lg bg-slate-100">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-slate-900 group-hover:text-[#022d47] transition-colors">
                  {product.name}
                </CardTitle>
                <CardDescription className="text-sm">
                  {product.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Grades */}
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Available Grades:</h4>
                  <div className="flex flex-wrap gap-1">
                    {product.grades.slice(0, 4).map((grade) => (
                      <Badge key={grade} variant="secondary" className="text-xs">
                        {grade}
                      </Badge>
                    ))}
                    {product.grades.length > 4 && (
                      <Badge variant="secondary" className="text-xs">
                        +{product.grades.length - 4} more
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Applications */}
                <div>
                  <h4 className="font-semibold text-sm text-slate-700 mb-2">Key Applications:</h4>
                  <div className="space-y-1">
                    {product.applications.slice(0, 2).map((app) => (
                      <div key={app} className="flex items-center space-x-2 text-xs text-slate-600">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        <span>{app}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full group-hover:border-[#022d47] group-hover:text-[#022d47]"
                  onClick={() => onProductSelect && onProductSelect(product.id)}
                >
                  View Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center bg-slate-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            Can't Find What You're Looking For?
          </h3>
          <p className="text-slate-600 mb-6">
            We maintain extensive inventory beyond our standard catalog. Contact our experts for custom requirements and specialized alloys.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-[#022d47] hover:bg-[#033858] text-white px-8">
              Request Custom Quote
            </Button>
            <Button 
              variant="outline" 
              className="hover:border-[#022d47] hover:text-[#022d47]"
              onClick={() => {
                const link = document.createElement('a');
                link.href = '/jps-metal-alloys-catalog.pdf';
                link.download = 'JPS-Metal-Alloys-Catalog.pdf';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Download Catalog
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};